import {
  createResourceComment,
  deleteResourceComment,
  fetchCommentUnreadSummary,
  fetchResourceComments,
  updateResourceComment,
  type ResourceCommentType,
} from '@/apis/resourceComments'

export interface ResourceCommentAuthor {
  id: string
  firstName: string | null
  lastName: string | null
  avatar: string | null
}

export interface ResourceComment {
  id: string
  body: string
  createdAt: string
  updatedAt: string
  readAt: string | null
  author: ResourceCommentAuthor
  isOwn: boolean
}

export interface ResourceCommentsListResult {
  items: ResourceComment[]
  total: number
}

export interface UnreadCommentSummaryItem {
  resourceType: ResourceCommentType
  resourceId: string
  teamId: string
  unreadCount: number
  lastCommentBody: string
  lastCommentAt: string
  missionName: string | null
  activityReportMonth: string | null
  freelancerFirstName: string | null
  freelancerLastName: string | null
}

function normalizeAuthor(raw: Record<string, unknown> | null | undefined): ResourceCommentAuthor {
  return {
    id: String(raw?.id ?? ''),
    firstName: (raw?.firstName as string | null) ?? (raw?.first_name as string | null) ?? null,
    lastName: (raw?.lastName as string | null) ?? (raw?.last_name as string | null) ?? null,
    avatar: (raw?.avatar as string | null) ?? null,
  }
}

function normalizeComment(raw: Record<string, unknown>): ResourceComment {
  const authorRaw = (raw.author as Record<string, unknown> | undefined) ?? {}
  return {
    id: String(raw.id),
    body: String(raw.body ?? ''),
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
    updatedAt: String(raw.updatedAt ?? raw.updated_at ?? ''),
    readAt: (raw.readAt as string | null) ?? (raw.read_at as string | null) ?? null,
    author: normalizeAuthor(authorRaw),
    isOwn: Boolean(raw.isOwn ?? raw.is_own),
  }
}

function normalizeUnreadItem(raw: Record<string, unknown>): UnreadCommentSummaryItem {
  return {
    resourceType: (raw.resourceType ?? raw.resource_type) as ResourceCommentType,
    resourceId: String(raw.resourceId ?? raw.resource_id ?? ''),
    teamId: String(raw.teamId ?? raw.team_id ?? ''),
    unreadCount: Number(raw.unreadCount ?? raw.unread_count ?? 0),
    lastCommentBody: String(raw.lastCommentBody ?? raw.last_comment_body ?? ''),
    lastCommentAt: String(raw.lastCommentAt ?? raw.last_comment_at ?? ''),
    missionName: (raw.missionName as string | null) ?? (raw.mission_name as string | null) ?? null,
    activityReportMonth:
      (raw.activityReportMonth as string | null) ?? (raw.activity_report_month as string | null) ?? null,
    freelancerFirstName:
      (raw.freelancerFirstName as string | null) ?? (raw.freelancer_first_name as string | null) ?? null,
    freelancerLastName:
      (raw.freelancerLastName as string | null) ?? (raw.freelancer_last_name as string | null) ?? null,
  }
}

export const resourceCommentService = {
  async getComments(params: {
    resourceType: ResourceCommentType
    resourceId: string
    limit?: number
    offset?: number
  }): Promise<ResourceCommentsListResult> {
    const response = await fetchResourceComments(params)
    const data = (response?.data ?? response) as Record<string, unknown>
    const payload = (data?.data ?? data) as Record<string, unknown>
    const itemsRaw = (payload?.items as Record<string, unknown>[]) ?? []
    return {
      items: itemsRaw.map(normalizeComment),
      total: Number(payload?.total ?? 0),
    }
  },

  async createComment(params: {
    resourceType: ResourceCommentType
    resourceId: string
    body: string
  }): Promise<ResourceComment> {
    const response = await createResourceComment({
      resource_type: params.resourceType,
      resource_id: params.resourceId,
      body: params.body,
    })
    const data = (response?.data ?? response) as Record<string, unknown>
    const payload = (data?.data ?? data) as Record<string, unknown>
    return normalizeComment(payload)
  },

  async updateComment(commentId: string, body: string): Promise<ResourceComment> {
    const response = await updateResourceComment(commentId, body)
    const data = (response?.data ?? response) as Record<string, unknown>
    const payload = (data?.data ?? data) as Record<string, unknown>
    return normalizeComment(payload)
  },

  async deleteComment(commentId: string): Promise<void> {
    await deleteResourceComment(commentId)
  },

  async getUnreadSummary(teamId: string): Promise<UnreadCommentSummaryItem[]> {
    const response = await fetchCommentUnreadSummary(teamId)
    const data = (response?.data ?? response) as Record<string, unknown>
    const payload = (data?.data ?? data) as Record<string, unknown>
    const itemsRaw = (payload?.items as Record<string, unknown>[]) ?? []
    return itemsRaw.map(normalizeUnreadItem)
  },
}

export default resourceCommentService
